import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "@mcleod/common";
import { Api, ModelRow, ObjectUtil } from "@mcleod/core";
import { StopEntry } from "./StopEntry";
import { OrderStopTable } from "./OrderStopTable";
import { AutogenLayoutManageStops } from "./autogen/AutogenLayoutManageStops";
import { TableRowMoveEvent, TableRowDisplayEvent, TableRow, Textbox, ClickEvent, ValidationResult, TableRowMode, Button, Table, Label, Panel, DataSourceMode, Snackbar, Layout, TableRowBeforeSaveEvent, TableContentsChangedEvent, BlurEvent, Dialog } from "@mcleod/components";
import { OrderStop } from "./OrderStop";
import { SelectStopsPrompt } from "./SelectStopsPrompt";
import { AppointmentUtil } from ".";

enum Mode {
    ADD,
    EDIT
}

interface StopDateError {
    stopNumber: number;
    dateField: string;
    message: string;
}

export class ManageStops extends AutogenLayoutManageStops {
    private _doAfterClose: (canceled: boolean) => void;
    public onSave = (): Promise<boolean> => this.rearrangeStops();
    public buttonRevert: Button;
    private _orderId: string;
    private _stopMode: Mode = null;
    private stopEntryCache = null;

    set stopMode(mode: Mode) {
        this._stopMode = mode;
        this.layoutStopEntry.enabled = mode != null;
        this.panelControlButtons.visible = mode != null;
        this.labelControlTitle.text =
            mode === Mode.EDIT ? "Edit Stop Details"
            : mode == Mode.ADD ? "Add Stop" : "Stop Info";
        this.buttonCancel.visible = mode == Mode.ADD;
        this.buttonInsertStop.visible = mode == Mode.ADD;
    }

    get stopMode(): Mode {
        return this._stopMode;
    }

    onLoad(): void | Promise<void> {
        this.setupOrderStopTable(this.layoutOrderStopTable);
        this.initializeDisabledStopEntry(this.layoutStopEntry);
        this.buttonRevert.addClickListener((event: ClickEvent) => this.buttonRevertOnClick(event));
    }

    setupOrderStopTable(layout: OrderStopTable) {
        layout.tableStop.addAfterTableRowMoveListener((event: TableRowMoveEvent) =>
            this.tableStopTableRowMove(event)
        );
        layout.tableStop.addRowDisplayListener(async (event: TableRowDisplayEvent) => {
            const tableRow = event.target;
            const modelRow = event.target.data as ModelRow;
            const textboxSchedArriveEarly = tableRow.findComponentById("textboxSchedArriveEarly") as Textbox;
            const textboxSchedArriveLate = tableRow.findComponentById("textboxSchedArriveLate") as Textbox;
            textboxSchedArriveEarly.addBlurListener((event: BlurEvent) => AppointmentUtil.checkAppointment(event));
            textboxSchedArriveLate.addBlurListener((event: BlurEvent) => AppointmentUtil.checkAppointment(event));
            this.setupDistanceToPreviousStop(tableRow);
            this.addDatePropagationClickListener(tableRow);
            this.addStopRowClickedListener(tableRow);
            this.setupButtons(tableRow);
        });
        layout.tableStop.addRowBeforeSaveListener((event: TableRowBeforeSaveEvent) => this.tableStopBeforeRowSave(event));
        layout.tableStop.addContentsChangedListener((event: TableContentsChangedEvent) => {
            this.clearEdits();
        });
        layout.tableStop.doOnRowDelete = () => this.clearEdits();
    }

    initializeDisabledStopEntry(layout: StopEntry) {
        this.stopEntryCache = { disabled: this.layoutStopEntry };
        layout.addLayoutLoadListener(event => {
            this.setupStopEntryLayout(layout);
            layout.textboxPickupDate.text = "";
            this.resetMode();
        });
    }

    setupStopEntryLayout(layout: StopEntry) {
        layout.getTimezone = true;
        layout.panelLocation.rowBreak = false;
        layout.panelLocation.paddingRight = 12;
        layout.textboxContactName.lookupModelLayoutHeight = 200;
        layout.locationChanged = () => this.handleStopDistances();
    }

    setupForDifferentStopType(modelRow: ModelRow) {
        if (modelRow.get("stop_type") == "SD") {
            this.layoutStopEntry.textboxStopType.enabled = false;
            this.layoutStopEntry.textboxStopType.printable = true;
            this.layoutStopEntry.textboxStopType.text = "Split Drop";
        }
        if (modelRow.get("stop_type") == "SP") {
            this.layoutStopEntry.textboxStopType.enabled = false;
            this.layoutStopEntry.textboxStopType.printable = true;
            this.layoutStopEntry.textboxStopType.text = "Split Pickup";
        }
        if (modelRow.get("stop_type") == "RP") {
            this.layoutStopEntry.textboxStopType.enabled = false;
            this.layoutStopEntry.textboxStopType.printable = true;
            this.layoutStopEntry.textboxStopType.text = "Reconsignment";
        }
        if (modelRow.get("is_reconsignment")) {
            this.layoutStopEntry.textboxStopType.enabled = false;
            this.layoutStopEntry.textboxStopType.printable = true;
            this.layoutStopEntry.textboxStopType.text = "Delivery";
        }
    }

    checkPreviousStopCleared(prevStop: ModelRow): void {
        if (prevStop == null) return;
        const stopCleared: boolean = !prevStop.isNull("actual_arrival") && !prevStop.isNull("actual_departure");
        this.layoutStopEntry.textboxActualArrival.visible =
        this.layoutStopEntry.textboxActualDeparture.visible = stopCleared;
        if (!stopCleared) {
            this.layoutStopEntry.textboxActualArrival.clear();
            this.layoutStopEntry.textboxActualDeparture.clear();
        }
    }

    setupStopNotesAndRefNo() {
        const stopNotesAndRefNo = this.layoutStopEntry.layoutStopNotesAndRefNbrs;
        OrderStop.setTableParentDataSource(stopNotesAndRefNo.tableStopComments, this.mainDataSource);
        OrderStop.setTableParentDataSource(stopNotesAndRefNo.tableStopReferenceNumbers, this.mainDataSource);
        stopNotesAndRefNo.stopRow = this.layoutStopEntry.activeRow;
    }

    addDatePropagationClickListener(tableRow: TableRow) {
        // We don't want to trigger the panelStop listener when only editing the dates
        const textboxSchedArriveEarly = tableRow.findComponentById("textboxSchedArriveEarly") as Textbox;
        const textboxSchedArriveLate = tableRow.findComponentById("textboxSchedArriveLate") as Textbox;
        const preventPropagation = (event: ClickEvent) => event.stopPropagation();
        textboxSchedArriveEarly.addClickListener(preventPropagation);
        textboxSchedArriveLate.addClickListener(preventPropagation);
    }

    addStopRowClickedListener(tableRow: TableRow) {
        const panelStop = tableRow.findComponentById("panelStop") as Panel;
        const panelStopInfo = tableRow.findComponentById("panelStopInfo") as Panel;
        panelStop.addClickListener((event: ClickEvent) => {
            const prevStop = tableRow.index > 0 ? this.layoutOrderStopTable.tableStop.rows[tableRow.index-1] : null;
            this.stopRowClicked(prevStop ? prevStop.data : null, tableRow.data, panelStopInfo);
        });
    }

    async hasNewEntryChanged(): Promise<boolean> {
        const newRow = this.layoutStopEntry.activeRow;
        if (newRow != null && !ObjectUtil.isEmptyObject(newRow.data) && !newRow.isDataEqual(newRow.originalData)) {
            const saveChanges = await CommonDialogs.showYesNo("Save your changes on this stop before switching?", "Save Changes");
            if (saveChanges == true) {
                this.insertStop();
                return true;
            }
        }
        return false;
    }

    resetMode() {
        // By setting the mode to UPDATE, we're mainly using this to _syncEnable() and refresh the components
        this.layoutStopEntry.sourceStop.mode = DataSourceMode.UPDATE;
    }

    async stopRowClicked(prevStop: ModelRow, modelRow: ModelRow, panelStopInfo: Panel) {
        if (panelStopInfo.borderColor == "primary") {
            // if already selected clear stopentry
            this.clearEdits();
            return;
        }

        if (this.stopMode == Mode.ADD && await this.hasNewEntryChanged()) {
            return;
        }

        let cachedLayout = this.stopEntryCache[modelRow.get("id")];
        if (cachedLayout == null) {
            cachedLayout = Layout.getLayout(this.layoutStopEntry.layoutName);
            this.stopEntryCache[modelRow.get("id")] = cachedLayout;
        }
        cachedLayout.addLayoutLoadListener(() => {
            this.panelStopEntry.replace(this.layoutStopEntry, cachedLayout);
            this.layoutStopEntry = cachedLayout;
            this.unselectInsertStops();
            this.stopMode = Mode.EDIT;
            this.setupStopEntryLayout(cachedLayout);
            this.layoutStopEntry.sourceStop.setRowsAndMode(modelRow._appending ? DataSourceMode.ADD : DataSourceMode.UPDATE, [modelRow]);
            this.checkPreviousStopCleared(prevStop);
            this.layoutStopEntry.textboxActualArrival.enabled = this.layoutStopEntry.textboxActualDeparture.enabled = false;
            if (this.layoutStopEntry.layoutStopNotesAndRefNbrs.stopRow == null) {
                this.layoutStopEntry.setupStopDateTypeByTimes(null);
                this.setupStopNotesAndRefNo();
            }
            this.layoutStopEntry.citystateCityStateZip.enabled = modelRow.isNull("location_id");
            this.styleRows(panelStopInfo);
            this.checkForChanges();
            this.setupForDifferentStopType(modelRow);
        });

    }

    styleRows(matchingPanel: Panel, removeStyling: boolean = false) {
        this.layoutOrderStopTable.tableStop.rows.forEach(row => {
            const panelStop = row.findComponentById("panelStopInfo") as Panel;
            if (panelStop != null) {
                panelStop.borderColor = panelStop == matchingPanel && !removeStyling ? "primary" : "subtle.light";
                const textboxSchedArriveEarly = row.findComponentById("textboxSchedArriveEarly") as Textbox;
                const textboxSchedArriveLate = row.findComponentById("textboxSchedArriveLate") as Textbox;
                textboxSchedArriveEarly.printable = textboxSchedArriveLate.printable = !removeStyling;
            }
        });
    }

    async rearrangeStops(): Promise<boolean> {
        this.buttonInsertStop.busy = true;
        const stops = this.layoutOrderStopTable.tableStop.dataSource.data;
        stops.forEach((stop: ModelRow) => {
            const stopEntry: StopEntry = this.stopEntryCache[stop.get("id")];
            if (stopEntry != null && this.stopNotesOrRefNoChanged(stopEntry)) {
                stop.set("stop_notes", stopEntry.layoutStopNotesAndRefNbrs.tableStopComments.data);
                stop.set("reference_numbers", stopEntry.layoutStopNotesAndRefNbrs.tableStopReferenceNumbers.data);
            }
        });
        try {
            const response = await Api.post("lme/dispatch/asset/reorganize-stops",
                {
                    "order_id": this.orderId,
                    "stops": stops
                });
            if (response?.data?.[0]?.success) {
                this.close(false);
                return true;
            } else {
                this.buttonInsertStop.busy = false;

                if (response?.data?.[0]?.errors?.length > 0) {
                    const scheduledArrivalErrorPrefix = "Scheduled arrival on stop ";
                    const lateArrivalErrorPrefix = "Late arrival on stop ";
                    const stopErrors: StopDateError[] = response.data[0].errors
                    .filter((s: string) => s.startsWith(scheduledArrivalErrorPrefix) || s.startsWith(lateArrivalErrorPrefix))
                    .map((s: string) => {
                        if(s.startsWith(scheduledArrivalErrorPrefix)) {
                            const stopNumber: number = +s.substring(scheduledArrivalErrorPrefix.length).split(" ")[0];
                            const dateTextboxId = "textboxSchedArriveEarly";
                            return {stopNumber: stopNumber, dateField: dateTextboxId, message: s}

                        } else if (s.startsWith(lateArrivalErrorPrefix)) {
                            const stopNumber: number = +s.substring(lateArrivalErrorPrefix.length).split(" ")[0];
                            const dateTextboxId = "textboxSchedArriveLate";
                            return {stopNumber: stopNumber, dateField: dateTextboxId, message: s}
                        }
                    });
                    this.highlightStopErrors(stopErrors);
                    const errorMessage = response.data[0].errors.join("\n");
                    CommonDialogs.showError(errorMessage);
                } else {
                    const errorMessage = "Unexpected problem rearranging stops.";
                    CommonDialogs.showError(errorMessage);
                }
            }
        } catch (e) {
            CommonDialogs.showError(e);
        }
        this.buttonInsertStop.busy = false;
        return false;
    }

    highlightStopErrors(stopErrors: StopDateError[]) {
        const allRows: TableRow[] = this.layoutOrderStopTable.tableStop.allRows;
        stopErrors.forEach(error => {
            const stopRow = allRows[error.stopNumber - 1];
            const dateField = stopRow.findComponentById(error.dateField) as Textbox;
            if (dateField) {
                dateField.validationWarning = error.message;
            }
        })
    }

    async buttonRevertOnClick(event: ClickEvent) {
        const editedRows = this.getEditedStops();
        if (editedRows.length == 0) {
            Snackbar.showWarningSnackbar("There are no changes to revert.");
            return;
        }
        const result = await SelectStopsPrompt.showDialog(editedRows, (stopRow: ModelRow) => this.revertStopNotesAndRefnbrs(stopRow));
        if (result == true) {
            this.handleStopDistances();
            this.checkForChanges();
            this.clearEdits();
        }
    }

    async buttonCancelOnClick(event: ClickEvent) {
        this.clearEdits();
    }

    revertStopNotesAndRefnbrs(stopRow: ModelRow) {
        const stopEntry = this.stopEntryCache[stopRow.get("id")];
        const stopNotesAndRefNo = stopEntry.layoutStopNotesAndRefNbrs;
        stopNotesAndRefNo.removeTableRowCreateListeners();
        stopNotesAndRefNo.tableStopComments.dataSource.clear();
        stopNotesAndRefNo.tableStopReferenceNumbers.dataSource.clear();
        stopNotesAndRefNo.stopRow = null;
    }

    clearEdits() {
        this.stopMode = null;
        this.unselectAll();
        this.unselectInsertStops();
        const disabledStopEntry = this.stopEntryCache["disabled"];
        this.panelStopEntry.replace(this.layoutStopEntry, disabledStopEntry);
        this.layoutStopEntry = disabledStopEntry;
    }

    /** This is an event handler for the onClick event of buttonInsertStop. */
    buttonInsertStopOnClick(event: ClickEvent) {
        this.insertStop();
    }

    stopsAreValid() {
        for (const key in this.stopEntryCache) {
            if (key != "disabled" && !this.stopEntryCache[key].mainDataSource.validate()) {
                return false;
            }
        }
        return true;
    }

    addedOrEdited() {
        let hasChanged = false;
        this.layoutOrderStopTable.tableStop.data.forEach((row: ModelRow) => {
            if (row._appending || this.editRowChanged(row)) {
                hasChanged = true;
            }
        });
        return hasChanged;
    }

    getEditedStops(): ModelRow[] {
        return this.layoutOrderStopTable.tableStop.data.filter((row: ModelRow) => !row._appending && this.editRowChanged(row));
    }

    stopNotesOrRefNoChanged(stopEntry: StopEntry = this.layoutStopEntry): boolean {
        // Since the StopNoteandRefno layout's parent datasource is the mainDataSource (SaveButton change listener)
        // instead of the layoutStopEntry's sourceStop, we need a way to check if the nested layout has changed.
        return stopEntry.layoutStopNotesAndRefNbrs.tableStopComments.dataSource.hasChanged()
            || stopEntry.layoutStopNotesAndRefNbrs.tableStopReferenceNumbers.dataSource.hasChanged();
    }

    editRowChanged(modelRow: ModelRow) {
        const onlyOrderSequence = Object.entries(modelRow.getChangedData()).length > 1 ? false : modelRow.getChangedData()?.order_sequence != null;
        const stopEntry = this.stopEntryCache[modelRow.get("id")];
        return stopEntry != null &&
            !onlyOrderSequence && (stopEntry.sourceStop.hasChanged() || this.stopNotesOrRefNoChanged(stopEntry));
    }

    checkForChanges() {
        let showCancel: boolean = false;
        this.layoutOrderStopTable.tableStop.rows.forEach(row => {
            const modelRow = row.data as ModelRow;
            const labelActionHistory = row.findComponentById("labelActionHistory") as Label;
            if (labelActionHistory && !modelRow._appending) {
                // if only order sequence is changed we don't add the EDITED STOP text
                const stopEntry = this.stopEntryCache[modelRow.get("id")];
                if (stopEntry != null) {
                    const hasChanged = this.editRowChanged(modelRow);
                    if (hasChanged) showCancel = true;
                    labelActionHistory.visible = hasChanged;
                    labelActionHistory.text = "EDITED STOP";
                }
            }
        });
        this.buttonRevert.visible = showCancel;
    }

    handleButtonVisibility() {
        this.layoutOrderStopTable.tableStop.allRows.forEach(row => {
            const buttonReconsignment = row.findComponentById("buttonReconsignment") as Button;
            if (buttonReconsignment) {
                buttonReconsignment.visible = false;
            }
                

        });
    }

    handleStopDistances() {
        this.layoutOrderStopTable.tableStop.allRows.forEach(row => {
            this.setupDistanceToPreviousStop(row);
        });
    }

    insertStop() {
        const stopEntryRow = this.layoutStopEntry.getStop();
        let invalidValidationResults: Partial<ValidationResult>[] = this.layoutStopEntry.validate(false)
            .filter(result => !result.isValid)
            .map(validationResult => ({
                validationMessage: validationResult.validationMessage,
                caption: validationResult.caption ?? validationResult.component.displayLabel ?? validationResult.component["caption"],
                component: validationResult.component
            }));

        invalidValidationResults = this.combineSimilarErrors(invalidValidationResults, "actual_arrival", "actual_departure");
        invalidValidationResults = this.combineSimilarErrors(invalidValidationResults, "sched_arrive_early", "sched_arrive_late");

        const validationResults = invalidValidationResults.map(
            invalid => invalid.caption ? `${invalid.caption} ${invalid.validationMessage}` : invalid.validationMessage
        );

        const distinctResults = [...new Set(validationResults)];

        const selectedStopData: ModelRow[] = this.layoutOrderStopTable.getSelectedStopData();
        const selectedRow: ModelRow = (selectedStopData?.length)
            ? selectedStopData[0]
            : null;
        if (!selectedRow) {
            distinctResults.push("Must select a Stop to insert before.");
        }

        if (distinctResults?.length) {
            CommonDialogs.showError(distinctResults.join('\n'), "Cannot Insert Stop");
            return;
        }
        this.layoutOrderStopTable.clearSelected();
        const selectedRowOrderSeq = selectedRow.getNumber("order_sequence");
        const tableStop = this.layoutOrderStopTable.tableStop;
        const prevRow = tableStop.rows[selectedRowOrderSeq-2];
        const nextRow = tableStop.rows[selectedRowOrderSeq-1];
        const appendedMessage = "Please select a different stop position to insert the new stop."
        if (this.layoutOrderStopTable.validClearedArrangement(prevRow, nextRow, stopEntryRow, appendedMessage)) {
            tableStop.dataSource.createBlankRow().then(row => {
                row.set({...stopEntryRow.data, order_sequence: selectedRowOrderSeq});
                row._appending = true;

                row.set("stop_notes", this.layoutStopEntry.layoutStopNotesAndRefNbrs.tableStopComments.data);
                row.set("reference_numbers", this.layoutStopEntry.layoutStopNotesAndRefNbrs.tableStopReferenceNumbers.data);
                if (!row.isNull("actual_departure")) {
                    row.set("status", "D");
                }
                tableStop.addRow(row, {virtualized: true, mode: TableRowMode.NONE}, {addToData: true, display: true});
                tableStop.moveRow(tableStop.allRows[tableStop.allRows.length - 1], selectedRowOrderSeq - 1);
                tableStop.allRows.forEach(row => this.layoutOrderStopTable.positionDragHandle(row));
            });
        }
    }

    combineSimilarErrors(invalidValidationResults: Partial<ValidationResult>[], field1: string, field2: string) {
        let relatedErrors = invalidValidationResults.filter(result =>
            result.validationMessage.indexOf("Required") < 0
            && result.component?.field
            && (result.component.field == field1 || result.component.field == field2)
        );
        for(let i = 0; i < relatedErrors.length; i++) {
            const error = relatedErrors[i];
            const duplicateErrors = relatedErrors.filter(result => result.validationMessage === error.validationMessage);
            if (duplicateErrors.length > 0) {
                relatedErrors = relatedErrors.filter(result => !duplicateErrors.includes(result));
                invalidValidationResults = invalidValidationResults.filter(result => !duplicateErrors.includes(result));
                invalidValidationResults.push({validationMessage: error.validationMessage, component: null, caption: null})
            }
        }
        return invalidValidationResults;
    }

    setStopDateCaptions(early: Textbox, isDelivery: boolean, isDateWindow: boolean) {
        const caption = isDelivery ? "Delivery" : "Pickup";
        early.caption = isDateWindow ? `${caption} Window` : `${caption} Appointment`;
    }

    setupButtons(tableRow: TableRow) {
        const buttonStopSelected = tableRow.findComponentById("buttonStopSelected") as Button;
        if (buttonStopSelected) {
            if (tableRow.data.get("is_reconsignment")) {
                buttonStopSelected.visible = false;
                const panelStopInfo = tableRow.findComponentById("panelStopInfo") as Panel;
                const prevStop = tableRow.index > 0 ? this.layoutOrderStopTable.tableStop.rows[tableRow.index-1] : null;
                this.stopRowClicked(prevStop ? prevStop.data : null, tableRow.data, panelStopInfo);
            }
            else {
                buttonStopSelected.addClickListener((event: ClickEvent) => this.buttonStopSelectedOnClick(event));
            }
        }
        const buttonReconsignment = tableRow.findComponentById("buttonReconsignment") as Button;
        if (buttonReconsignment && tableRow.data.get("reconsignable")) {
            buttonReconsignment.visible = true;
            buttonReconsignment.addClickListener((event: ClickEvent) => this.buttonReconsignmentOnClick(event));
        }
    }

    unselectAll() {
        this.layoutOrderStopTable.tableStop.selectedRows = [];
        this.styleRows(null, true);
    }

    unselectInsertStops() {
        this.layoutOrderStopTable.tableStop.rows.forEach(row => {
            const buttonStopSelected = row.findComponentById("buttonStopSelected") as Button;
            if (buttonStopSelected) {
                buttonStopSelected.applyTempState({
                    caption: null,
                    imageName: ImageName.add
                });
                buttonStopSelected.selected = false;
            }
        });
    }

    async buttonStopSelectedOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const button = event.target as Button;
        if (button.selected) {
            this.unselectAll();
            this.unselectInsertStops();
            if (!this.layoutStopEntry.activeRow || !this.layoutStopEntry.activeRow._appending || this.isStopEntryAdded()) {
                const insertLayout = Layout.getLayout(this.layoutStopEntry.layoutName) as StopEntry;
                insertLayout.addLayoutLoadListener(async () => {
                    this.panelStopEntry.replace(this.layoutStopEntry, insertLayout);
                    this.layoutStopEntry = insertLayout;
                    this.stopMode = Mode.ADD;
                    this.setupStopEntryLayout(insertLayout);
                    this.checkPreviousStopCleared(this.layoutOrderStopTable.tableStop.data[tableRow.index-1]);
                    await this.layoutStopEntry.newEntry();
                });
            } else {
                this.checkPreviousStopCleared(this.layoutOrderStopTable.tableStop.data[tableRow.index-1]);
            }
            button.applyTempState({
                caption: "Selected",
                imageName: ImageName.circlePlusFilled
            });
            button.selected = true;
        } else {
            button.applyTempState({
                caption: null,
                imageName: ImageName.add
            });
            this.stopMode = null;
            this.layoutStopEntry.sourceStop.data = [];
            this.resetMode();
        }
    }

    async buttonReconsignmentOnClick(event: ClickEvent) {
            const tableRow = TableRow.getContainingTableRow(event.target as Button);
            const modelRow = tableRow.data;
            if (!(modelRow.get("recon_warnings", "") == "")) {
                const isYes = await CommonDialogs.showYesNo(modelRow.getString("recon_warnings"), "Reconsign Anyway?");
                if (!isYes) {
                    return;
                }
            }
            modelRow.set("stop_type", "RP")
            modelRow.set("should_reconsign", true);
            const buttonReconsignment = tableRow.findComponentById("buttonReconsignment") as Button;
            if (buttonReconsignment) {
                buttonReconsignment.visible = false;
            }
            this.setupForDifferentStopType(modelRow);
            const tableStop = this.layoutOrderStopTable.tableStop;
            tableStop.dataSource.createBlankRow().then(row => {
                row._appending = true;
                row.set("is_reconsignment", true);
                row.set("stop_type", "SO");
                tableStop.addRow(row, {virtualized: true, mode: TableRowMode.NONE}, {addToData: true, display: true});
            });
    }

    isStopEntryAdded() {
        // At this point, we need to check if the stop entry on the right hand side is in add mode, and if it is, check if it's already been inserted into the table
        // If so, we want to create a new insert layout and replace the right hand side.
        return this.layoutStopEntry.activeRow._appending &&
            this.layoutOrderStopTable.tableStop.data.some(row => row.get("id") == this.layoutStopEntry.activeRow.get("id"));
    }

    tableStopBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        const stopEntry:StopEntry = this.stopEntryCache[modelRow.get("id")];
        if (stopEntry != null) {
            stopEntry.layoutStopNotesAndRefNbrs.saveChangesToRow(modelRow);
            if (!modelRow.isNull("appointment_change")) {
                const changedData = modelRow.getChangedData();
                const apptChangedRow = modelRow.get("appointment_change");
                apptChangedRow.set("revised_early", changedData["sched_arrive_early"] ?? null);
                apptChangedRow.set("revised_late", changedData["sched_arrive_late"] ?? null);
            }
        }
    }

    tableStopTableRowMove(event: TableRowMoveEvent) {
        this.clearTextboxChildrenValidationWarning(event.getTableRow());
        this.handleButtonVisibility();
        this.handleStopDistances();
    }

    async setupDistanceToPreviousStop(tableRow: TableRow) {
        const stopRow = tableRow.data;
        const panelRowTopper = tableRow.findComponentById("panelRowTopper");
        if (panelRowTopper) {
            if (tableRow.index === 0) {
                panelRowTopper.visible = false;
            } else {
                panelRowTopper.visible = true;
                const buttonStopSelected = tableRow.findComponentById("buttonStopSelected") as Button;
                buttonStopSelected.visible = stopRow.get("stop_type") != "SP" && !stopRow.get("is_reconsignment");
                const priorRow = tableRow.table.getRowByIndex(tableRow.index - 1);
                const priorStop = priorRow.data as ModelRow;
                const distCalcProps = {
                    first_city: stopRow.get("city_id"),
                    first_state: stopRow.get("state"),
                    first_zip: stopRow.get("zip_code"),
                    prior_city: priorStop.get("city_id"),
                    prior_state: priorStop.get("state"),
                    prior_zip: priorStop.get("zip_code")
                };
                const labelDistBetweenStops = tableRow.findComponentById("labelDistBetweenStops") as Label;
                if (labelDistBetweenStops && distCalcProps.first_zip != null) {
                    labelDistBetweenStops.text = "0 MILES BETWEEN STOPS";
                    const res = await Api.post("lme/dispatch/distance-between-stops", distCalcProps);
                    if (res.data?.[0].calc_dist_from_prev) {
                        labelDistBetweenStops.text = `${res.data?.[0].calc_dist_from_prev} MILES BETWEEN STOPS`;
                    }
                }
            }
        }
    }

    clearTextboxChildrenValidationWarning(row: TableRow) {
        row.forEveryChildComponent(comp => {
            if (comp instanceof Textbox && comp.validationWarning) {
                comp.validationWarning = null;
            }
        })
    }

    close(canceled: boolean) {
        this.slideOut().then(() => {
            if (this.doAfterClose != null)
                this.doAfterClose(canceled);
        });
    }

    public set doAfterClose(f) {
        this._doAfterClose = f;
    }

    public get doAfterClose() {
        return this._doAfterClose;
    }

    public get orderId() {
        return this._orderId;
    }

    public set orderId(id) {
        this._orderId = id;
        this.layoutStopEntry.orderId = id;
    }
}
